import usePermissions from "@/hooks/use-permissions.hook.ts";
import { hasReportsAccessForEmployee } from "@/pages/reporting/reporting.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useAppConfig, useMenuCounters } from "@/queries/settings/use-app.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import * as Sentry from "@sentry/react";
import { SidebarRailItemData } from "@skailer-com/uikit";
import i18n from "i18next";

export interface MenuConfigItem extends SidebarRailItemData {
  permissions?: ApiPermission[];
}

export default function useSidebarMenuTop() {
  const { data: employee } = useEmployeeMe();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const { data: menuCounters } = useMenuCounters();
  const { data: appConfig } = useAppConfig();

  const canViewOrganizationFolders = !!appConfig?.ui.settings.can_view_organization_folders;

  function getMenuAll() {
    if (!employee) return [];

    const items: MenuConfigItem[] = [];
    items.push(
      ...[
        {
          key: "home",
          label: i18n.t("core:home"),
          icon: "fa-house",
          to: "/home",
          matchPrefixes: ["/home"],
        },
        {
          key: "people",
          label: i18n.t("core:people"),
          icon: "fa-users",
          to: "/employees",
          matchPrefixes: [
            "/employees",
            "/departments",
            "/time-off",
            "/account-settings",
            "/email-confirmed",
            "/news",
          ],
          permissions: [ApiPermission.see_employee_directory],
        },
      ]
    );

    if (isFeatureEnabled(ApiFeatureType.ats)) {
      items.push({
        key: "recruitment",
        label: i18n.t("ats:recruit"),
        icon: "fa-user-magnifying-glass",
        to: "/recruitment/candidates",
        matchPrefixes: ["/recruitment", "/feedbacks"],
        permissions: [ApiPermission.recruiting_module],
      });
    }

    if (isFeatureEnabled(ApiFeatureType.talent_surveys) && canIEdit(ApiPermission.surveys_module)) {
      items.push({
        key: "surveys",
        label: i18n.t("surveys:surveys"),
        icon: "fa-ballot-check",
        to: "/surveys/requests",
        matchPrefixes: ["/surveys", "/templates/surveys"],
        count: menuCounters?.survey?.requests.count,
      });
    }

    if (
      isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
      canIEdit(ApiPermission.performance_reviews_module)
    ) {
      items.push({
        key: "performance-reviews",
        label: i18n.t("performance_review:performance_reviews"),
        icon: "fa-gauge-high",
        to: "/my-feedback/requests",
        matchPrefixes: ["/my-feedback", "/performance-reviews", "/templates/performance-reviews"],
        count: menuCounters?.performance_review?.requests.count ?? 0,
      });
    }

    if (isFeatureEnabled(ApiFeatureType.goals) && canIEdit(ApiPermission.goals_module)) {
      items.push({
        key: "goals",
        label: i18n.t("goals:goals"),
        icon: "fa-bullseye",
        to: "/goals/personal",
        matchPrefixes: ["/goals"],
      });
    }

    if (canViewOrganizationFolders) {
      items.push({
        key: "documents",
        label: i18n.t("core:documents"),
        icon: "fa-folder",
        to: "/docs",
        matchPrefixes: ["/docs"],
      });
    }
    if (hasReportsAccessForEmployee(employee)) {
      items.push({
        key: "reporting",
        label: i18n.t("common:reports"),
        icon: "fa-circle-three-quarters-stroke",
        to: "/reporting",
        matchPrefixes: ["/reporting"],
      });
    }

    if (isFeatureEnabled(ApiFeatureType.workflows) && canIEdit(ApiPermission.workflow_module)) {
      items.push({
        key: "workflows",
        label: i18n.t("workflow:workflows"),
        icon: "fa-diagram-project",
        to: "/workflows/library",
        matchPrefixes: ["/workflows"],
      });
    }

    if (isFeatureEnabled(ApiFeatureType.core_company_calendar)) {
      items.push({
        key: "calendar",
        label: i18n.t("common:calendar"),
        icon: "fa-calendar",
        to: "/calendar",
        matchPrefixes: ["/calendar"],
      });
    }

    return items;
  }

  function getMenu() {
    try {
      if (!employee) return [];
      if (!employee.api_permissions) {
        return [];
      }

      const menuConfigTop = getMenuAll();
      const items: MenuConfigItem[] = [];

      for (const item of menuConfigTop) {
        try {
          let hasPermission = true;

          if (item.permissions?.length) {
            hasPermission = item.permissions.some((permission) => canIEdit(permission));
          }

          if (hasPermission) {
            items.push(item);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      }
      return items;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  return { getMenu, menuCounters, canViewOrganizationFolders };
}

export interface SidebarConfigItem {
  to: string;
  title: string;
  isActive: boolean;
  count?: number;
  emoji?: string;
  subtitle?: string;
}
